<template>
  <article
    class="support-ticket-card"
    :class="[
      { 'support-ticket-card--unread': ticket.is_unread },
      ticket.status === 'new' ? `support-ticket-card--age-${newAgeTone}` : null
    ]"
    @click="$emit('open', ticket)"
  >
    <header>
      <div class="support-ticket-card__identity">
        <span class="support-ticket-card__number">{{ ticket.public_number }}</span>
        <app-status-badge
          v-if="ticket.system"
          design="new"
          compact
          :label="ticket.system.name"
          :color="ticket.system.color"
        />
      </div>
      <span v-if="ticket.is_unread" class="support-ticket-card__unread"><i></i>Unread</span>
      <time v-if="ticket.status === 'new'" class="support-ticket-card__age" :class="`support-ticket-card__age--${newAgeTone}`">
        <i></i>{{ newAgeLabel }}
      </time>
      <time v-else>{{ relativeTime(ticket.last_activity_at) }}</time>
    </header>
    <h3><button type="button" :aria-label="`Open ${ticket.public_number}: ${ticket.subject}`" @click.stop="$emit('open', ticket)">{{ ticket.subject }}</button></h3>

    <div v-if="showSupportMeta && (ticket.priority !== 'medium' || ticket.is_important)" class="support-ticket-card__support-meta">
      <ticket-priority-indicator :priority="ticket.priority" hide-medium />
      <span v-if="ticket.is_important" class="support-ticket-card__important" title="Important ticket" aria-label="Important ticket">
        <img src="@/assets/img/star-full.svg" alt="">
      </span>
    </div>

    <div class="support-ticket-card__people" :class="{ 'support-ticket-card__people--assignee-only': !showRequester }">
      <div v-if="showRequester" class="support-ticket-card__person" :title="ticket.requester ? ticket.requester.full_name : 'Unknown requester'">
        <img :src="avatarFor(ticket.requester)" alt="" @error="avatarError">
        <span><small>Requester</small><strong>{{ ticket.requester ? ticket.requester.full_name : 'Unknown' }}</strong></span>
      </div>
      <div class="support-ticket-card__person" :class="{ 'support-ticket-card__person--empty': !ticket.assignee }" :title="ticket.assignee ? ticket.assignee.full_name : 'Unassigned'">
        <img :src="avatarFor(ticket.assignee)" alt="" @error="avatarError">
        <span><small>Assignee</small><strong>{{ ticket.assignee ? ticket.assignee.full_name : 'Unassigned' }}</strong></span>
      </div>
    </div>

    <div v-if="hasTaxonomy" class="support-ticket-card__taxonomy">
      <div v-if="ticket.tags && ticket.tags.length" class="support-ticket-card__taxonomy-row">
        <span>Tags</span>
        <div>
          <app-status-badge
            v-for="tag in visibleTags"
            :key="`tag-${tag.id}`"
            design="new"
            compact
            :label="tag.name"
            :color="tag.color"
          />
          <span v-if="hiddenTagCount" class="support-ticket-card__more">+{{ hiddenTagCount }}</span>
        </div>
      </div>
      <div v-if="ticket.responsible_labels && ticket.responsible_labels.length" class="support-ticket-card__taxonomy-row support-ticket-card__taxonomy-row--responsibility">
        <span>Responsibility</span>
        <div>
          <app-status-badge
            v-for="label in visibleResponsibleLabels"
            :key="`label-${label.id}`"
            design="new"
            compact
            :label="label.name"
            :color="label.color"
          />
          <span v-if="hiddenResponsibleLabelCount" class="support-ticket-card__more">+{{ hiddenResponsibleLabelCount }}</span>
        </div>
      </div>
    </div>

    <footer v-if="ticket.checklist.total">
      <div class="support-ticket-card__checklist">
        <span>Checklist <strong>{{ ticket.checklist.completed }}/{{ ticket.checklist.total }}</strong></span>
        <div role="progressbar" :aria-valuenow="checklistProgress" aria-valuemin="0" aria-valuemax="100"><i :style="{ width: `${checklistProgress}%` }"></i></div>
      </div>
    </footer>
  </article>
</template>

<script>
import { relativeTime } from '../ticketState'
import TicketPriorityIndicator from './TicketPriorityIndicator'

export default {
  name: 'SupportTicketCard',
  components: { TicketPriorityIndicator },
  props: {
    ticket: { type: Object, required: true },
    showRequester: { type: Boolean, default: true },
    showSupportMeta: { type: Boolean, default: true }
  },
  computed: {
    newAgeMinutes() {
      return Math.max(0, Math.floor((Date.now() - new Date(this.ticket.status_changed_at).getTime()) / 60000))
    },
    newAgeTone() {
      if (this.newAgeMinutes < 10) return 'green'
      if (this.newAgeMinutes < 30) return 'yellow'
      if (this.newAgeMinutes < 60) return 'orange'
      return 'red'
    },
    newAgeLabel() {
      if (this.newAgeMinutes < 1) return 'New · <1m'
      if (this.newAgeMinutes < 60) return `New · ${this.newAgeMinutes}m`
      const hours = Math.floor(this.newAgeMinutes / 60)
      const minutes = this.newAgeMinutes % 60
      return `New · ${hours}h${minutes ? ` ${minutes}m` : ''}`
    },
    hasTaxonomy() { return Boolean(this.ticket.tags?.length || this.ticket.responsible_labels?.length) },
    visibleTags() { return (this.ticket.tags || []).slice(0, 3) },
    hiddenTagCount() { return Math.max(0, (this.ticket.tags || []).length - this.visibleTags.length) },
    visibleResponsibleLabels() { return (this.ticket.responsible_labels || []).slice(0, 3) },
    hiddenResponsibleLabelCount() { return Math.max(0, (this.ticket.responsible_labels || []).length - this.visibleResponsibleLabels.length) },
    checklistProgress() {
      return this.ticket.checklist.total ? Math.round(this.ticket.checklist.completed / this.ticket.checklist.total * 100) : 0
    }
  },
  methods: {
    relativeTime,
    avatarFor(user) {
      return user?.crop?.crop_url || require('@/assets/img/user-avatar-placeholder.svg')
    },
    avatarError(event) {
      event.target.onerror = null
      event.target.src = require('@/assets/img/user-avatar-placeholder.svg')
    }
  }
}
</script>

<style lang="scss" scoped>
.support-ticket-card {
  position: relative;
  padding: $ui-space-12 $ui-space-16 $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface;
  box-shadow: $ui-card-shadow;
  color: $ui-text;
  cursor: pointer;
  font-family: $ui-font-body;
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;
}

.support-ticket-card:hover,
.support-ticket-card:focus-within {
  border-color: $ui-blue;
  box-shadow: 0 8px 24px rgba(15, 23, 42, .09);
  outline: 0;
  transform: translateY(-1px);
}

.support-ticket-card--unread { box-shadow: inset 4px 0 0 $ui-blue, $ui-card-shadow; }
.support-ticket-card header,
.support-ticket-card footer { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-8; }
.support-ticket-card header { color: $ui-muted; font-size: $ui-font-size-metadata; }
.support-ticket-card header { display: flex; min-height: 26px; align-items: center; flex-wrap: wrap; gap: $ui-space-8; }
.support-ticket-card header time { margin-left: auto; }
.support-ticket-card__identity { display: flex; min-width: 0; align-items: center; gap: 6px; }
.support-ticket-card__number { color: $ui-muted; font-weight: 600; letter-spacing: .02em; white-space: nowrap; }
.support-ticket-card__unread { display: inline-flex; align-items: center; gap: 4px; color: $ui-blue; font-weight: 700; }
.support-ticket-card__unread i { width: 7px; height: 7px; border-radius: 50%; background: $ui-blue; }
.support-ticket-card__age { display: inline-flex; align-items: center; gap: 5px; padding: 4px 8px; border-radius: 999px; font-weight: 800; }
.support-ticket-card__age i { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.support-ticket-card__age--green { background: rgba(22, 163, 74, .12); color: #15803d; }
.support-ticket-card__age--yellow { background: rgba(202, 138, 4, .14); color: #a16207; }
.support-ticket-card__age--orange { background: rgba(234, 88, 12, .13); color: #c2410c; }
.support-ticket-card__age--red { background: rgba(220, 38, 38, .12); color: #b91c1c; }
.support-ticket-card h3 { margin: $ui-space-8 0 $ui-space-12; color: $ui-ink; font: 600 $ui-font-size-section-title $ui-font-heading; line-height: 1.35; }
.support-ticket-card h3 button { padding: 0; border: 0; background: transparent; color: inherit; cursor: pointer; font: inherit; line-height: inherit; text-align: left; }
.support-ticket-card h3 button:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; }
.support-ticket-card__support-meta { display: flex; min-height: 24px; align-items: center; gap: $ui-space-8; margin: -4px 0 $ui-space-12; }
.support-ticket-card__important { display: inline-flex; width: 24px; height: 24px; align-items: center; justify-content: center; border-radius: 50%; background: rgba($ui-amber, .14); }
.support-ticket-card__important img { width: 15px; height: 15px; }
.support-ticket-card__people { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-8; margin-bottom: $ui-space-12; }
.support-ticket-card__people--assignee-only { grid-template-columns: minmax(0, 1fr); }
.support-ticket-card__person { display: flex; min-width: 0; align-items: center; gap: $ui-space-8; }
.support-ticket-card__person img { width: 30px; height: 30px; flex: 0 0 30px; border: 2px solid $ui-surface; border-radius: 50%; box-shadow: 0 0 0 1px $ui-line; object-fit: cover; }
.support-ticket-card__person > span { display: grid; min-width: 0; }
.support-ticket-card__person small { color: $ui-muted; font-size: $ui-font-size-metadata; line-height: 1.2; text-transform: uppercase; }
.support-ticket-card__person strong { overflow: hidden; color: $ui-text; font-size: $ui-font-size-metadata; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.support-ticket-card__person--empty strong { color: $ui-muted; font-weight: 500; }
.support-ticket-card__person--empty img { filter: grayscale(1); opacity: .55; }
.support-ticket-card__taxonomy { display: grid; gap: 7px; }
.support-ticket-card__taxonomy-row { display: grid; min-width: 0; grid-template-columns: 72px minmax(0, 1fr); align-items: start; gap: 8px; }
.support-ticket-card__taxonomy-row > span { padding-top: 4px; color: $ui-muted; font-size: $ui-font-size-metadata; font-weight: 700; letter-spacing: .04em; line-height: 16px; text-transform: uppercase; }
.support-ticket-card__taxonomy-row > div { display: flex; min-width: 0; flex-wrap: wrap; gap: 4px; }
.support-ticket-card__taxonomy-row--responsibility { grid-template-columns: 92px minmax(0, 1fr); }
.support-ticket-card__more { display: inline-flex; min-height: 24px; align-items: center; padding: 2px $ui-space-8; border: 1px solid $ui-line; border-radius: $ui-control-radius; color: $ui-muted; font-size: $ui-font-size-metadata; font-weight: 700; }
.support-ticket-card footer { margin-top: $ui-space-12; padding-top: $ui-space-12; border-top: 1px solid $ui-line; color: $ui-muted; font-size: $ui-font-size-metadata; }
.support-ticket-card__checklist { display: grid; min-width: 90px; flex: 1; gap: 5px; }
.support-ticket-card__checklist > span { display: flex; justify-content: space-between; gap: $ui-space-8; }
.support-ticket-card__checklist > div { height: 5px; overflow: hidden; border-radius: 999px; background: rgba($ui-green, .14); }
.support-ticket-card__checklist i { display: block; height: 100%; border-radius: inherit; background: $ui-green; }
@media (max-width: 420px) { .support-ticket-card__people { grid-template-columns: 1fr; } }
</style>
